// AddPractitionerForm.jsx

import BootDatePicker from "components/common/custom/forms/BootDatePicker";
import TextInput from "components/common/custom/forms/BootTextField/TextField";
import MultiSelect from "components/common/MultiSelect";
import { GetAllDropDownData } from "features/eliglibility/eliglibility-check/config/eligibilityCheckApi";
import { GetAllNationality } from "features/preauthorization/config/preauthorizationApi";
import { useEffect } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  GetAllSpeciality,
  GetIndividualParctitioner,
} from "../config/practitionerSliceAPi";
import classNames from "classnames";
import { useParams } from "react-router-dom";
import { genderData, nationalityData } from "../utils";

// ... existing imports
const isEnglish = (value) => /^[A-Za-z0-9\s'-]+$/.test(value);
const isArabic = (value) => /^[\u0600-\u06FF\s]+$/.test(value);
const isValidDate = (date) => date instanceof Date && !isNaN(date.getTime());

const AddPractitionerForm = ({
  control,
  watch,
  setValue,
  errors,
  view,
  isSubmitted,
}) => {
  const dispatch = useDispatch();
  const { id } = useParams();

  const { genderList, maritalList } = useSelector(
    (state) => state.eligibilityCheck
  );
  const { nationalityPreList } = useSelector((state) => state.preauthorization);
  const { specilaityList, individualPractitioner } = useSelector(
    (state) => state.practitioner
  );
  const allBranches = JSON.parse(localStorage.getItem("branch_settings")) || [];

  // Fetch dropdowns if not already fetched
  useEffect(() => {
    if (!maritalList?.length) dispatch(GetAllDropDownData());
    if (!nationalityPreList?.length) dispatch(GetAllNationality());
    if (!specilaityList?.length) dispatch(GetAllSpeciality());
  }, []);

  // Fetch individual record if editing
  useEffect(() => {
    if (id) {
      dispatch(GetIndividualParctitioner(id));
    }
  }, [id]);

  // Set form values from individual practitioner
  useEffect(() => {
    if (!id || !individualPractitioner?.length) return;

    const data = individualPractitioner[0];

    const selectedBranches = data?.branch_id
      ?.split(",")
      .map((id) => parseInt(id.trim(), 10))
      .map((branchId) => {
        const branch = allBranches.find((b) => b.branch_id === branchId);
        return branch
          ? {
              value: branch.branch_id,
              label: branch.branch_name,
              branch_cchino: branch.branch_cchino,
            }
          : null;
      })
      .filter(Boolean);

    setValue("branch", selectedBranches);
    setValue("first_name_eng", data?.practitioner_firstname || "");
    setValue("last_name_eng", data?.practitioner_familyname || "");
    setValue("first_name_arabic", data?.practitioner_firstname_arabic || "");
    setValue("last_name_arabic", data?.practitioner_familyname_arabic || "");
    setValue("licence_number", data?.practitioner_license || "");
    setValue(
      "license_exp_date",
      data?.practitioner_license_exp
        ? new Date(data?.practitioner_license_exp)
        : null
    );
    setValue("email", data?.practitioner_emailid || "");

    const nationalityOption = nationalityPreList?.find(
      (item) => item.name === data?.practitioner_nationality
    );
    if (nationalityOption) {
      setValue("nationality", {
        code: nationalityOption.code,
        name: nationalityOption.name,
      });
    }

    const genderOption = genderList?.find(
      (item) => item.genders_disply === data?.practitioner_gender
    );
    if (genderOption) {
      setValue("gender", {
        value: genderOption.genders_code,
        label: genderOption.genders_disply,
      });
    }

    const maritalOption = maritalList?.find(
      (item) => item.maritalstatus_disply === data?.practitioner_maritalstatus
    );
    if (maritalOption) {
      setValue("marital_status", {
        maritalstatus_code: maritalOption.maritalstatus_code,
        maritalstatus_disply: maritalOption.maritalstatus_disply,
      });
    }

    setValue(
      "birth_date",
      data?.practitioner_dob ? new Date(data?.practitioner_dob) : null
    );

    const specialities = data?.practitioner_specialities?.split(",") || [];
    const matchedSpecs = specilaityList
      ?.filter((s) => specialities.includes(s.specialities_name))
      .map((s) => ({
        value: s.specialities_code,
        label: s.specialities_name,
      }));

    setValue("national_id", data?.practitioner_nationalid);
    setValue("mobile_no", data?.practitioner_mobilenumber);

    setValue("speciality", matchedSpecs);
  }, [individualPractitioner, id]);

  return (
    <Row>
      {/* First Name English */}
      <Col xs={12} sm={6} md={3} xl={2}>
        <TextInput
          name="first_name_eng"
          label="First Name (English)"
          control={control}
          errors={errors}
          disabled={view}
          required={true}
          size="sm"
          rules={{
            required: "First Name (English) is required",
            validate: (v) => isEnglish(v) || "Must be English letters only",
          }}
        />
      </Col>

      {/* Last Name English */}
      <Col xs={12} sm={6} md={3} xl={2}>
        <TextInput
          name="last_name_eng"
          label="Last Name (English)"
          control={control}
          errors={errors}
          disabled={view}
          required={true}
          size="sm"
          rules={{
            required: "Last Name (English) is required",
            validate: (v) => isEnglish(v) || "Must be English letters only",
          }}
        />
      </Col>

      {/* First Name Arabic */}
      <Col xs={12} sm={6} md={3} xl={2}>
        <TextInput
          name="first_name_arabic"
          label="First Name (Arabic)"
          control={control}
          disabled={view}
          errors={errors}
          size="sm"
          rules={{
            validate: (v) => !v || isArabic(v) || "Must be Arabic letters only",
          }}
        />
      </Col>

      {/* Last Name Arabic */}
      <Col xs={12} sm={6} md={3} xl={2}>
        <TextInput
          name="last_name_arabic"
          label="Last Name (Arabic)"
          control={control}
          errors={errors}
          disabled={view}
          size="sm"
          rules={{
            validate: (v) => !v || isArabic(v) || "Must be Arabic letters only",
          }}
        />
      </Col>

      {/* License Number */}
      <Col xs={12} sm={6} md={3} xl={2}>
        <TextInput
          name="licence_number"
          label="License number"
          control={control}
          disabled={view}
          errors={errors}
          required={true}
          size="sm"
        />
      </Col>

      {/* License Expiry Date */}
      <Col xs={12} sm={6} md={3} xl={2}>
        <BootDatePicker
          name="license_exp_date"
          label="License Exp. Date"
          control={control}
          size="sm"
          disabled={view}
          selected={watch("license_exp_date")}
          onChange={(date) => setValue("license_exp_date", date)}
          errors={errors}
          isRequired={false}
          rules={{
            validate: (v) =>
              !v || isValidDate(v) || "Invalid license expiry date",
          }}
        />
      </Col>

      {/* Gender (Required) */}
      <Col xs={12} sm={6} md={3} xl={2}>
        <label className="form-label mb-2">
          Gender <span className="text-danger">*</span>
        </label>
        <Controller
          name="gender"
          control={control}
          rules={{ required: "Gender is required" }}
          render={({ field }) => (
            <MultiSelect
              {...field}
              isMulti={false}
              options={genderList?.map((g) => ({
                value: g.genders_code,
                label: g.genders_disply,
              }))}
              disabled={view}
              value={field.value}
              onChange={(val) => setValue("gender", val ? val : null)}
              className={classNames({
                "is-invalid": errors.gender,
              })}
              placeholder="Choose ..."
            />
          )}
        />
        {errors.gender && <Form.Control.Feedback type="invalid" />}
      </Col>

      {/* Nationality (Required) */}
      <Col xs={12} sm={6} md={3} xl={2}>
        <label className="form-label mb-2">
          Nationality <span className="text-danger">*</span>
        </label>
        <Controller
          name="nationality"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <MultiSelect
              {...field}
              menuPlacement="auto"
              closeMenuOnSelect={false}
              disabled={view}
              isMulti={false}
              options={nationalityPreList?.map((item) => ({
                value: item.code,
                label: item.name,
              }))}
              isClearable={true}
              value={
                field.value
                  ? {
                      value: field.value.code,
                      label: field.value.name,
                    }
                  : null
              }
              className={classNames({
                "is-invalid":
                  errors.nationality || (isSubmitted && !watch("nationality")),
              })}
              onChange={(selected) => {
                setValue(
                  "nationality",
                  selected
                    ? {
                        code: selected.value,
                        name: selected.label,
                      }
                    : null
                );
              }}
              formatOptionLabel={({ label }) => <div>{label}</div>}
              getOptionValue={(option) => option.value}
              placeholder="Choose ..."
            />
          )}
        />
      </Col>

      {/* DOB */}
      <Col xs={12} sm={6} md={3} xl={2}>
        <BootDatePicker
          name="birth_date"
          label="Birth Date"
          control={control}
          disabled={view}
          size="sm"
          selected={watch("birth_date")}
          onChange={(date) => setValue("birth_date", date)}
          errors={errors}
          maxDate={new Date()}
          isRequired={false}
          rules={{
            validate: (v) => !v || isValidDate(v) || "Invalid birth date",
          }}
        />
      </Col>
      <Col xs={12} sm={6} md={3} xl={2}>
        <label className="form-label">Marital Status</label>
        <Controller
          name="marital_status"
          control={control}
          rules={{ required: false }}
          render={({ field }) => (
            <MultiSelect
              {...field}
              menuPlacement="auto"
              closeMenuOnSelect={false}
              disabled={view}
              isMulti={false}
              options={maritalList?.map((marital) => ({
                value: marital?.maritalstatus_code,
                label: marital?.maritalstatus_disply,
              }))}
              value={
                field.value
                  ? {
                      value: field.value.maritalstatus_code,
                      label: field.value.maritalstatus_disply,
                    }
                  : null
              }
              onChange={(selected) => {
                setValue(
                  "marital_status",
                  selected
                    ? {
                        maritalstatus_code: selected.value,
                        maritalstatus_disply: selected.label,
                      }
                    : null
                );
              }}
              formatOptionLabel={({ label }) => <div>{label}</div>}
              placeholder="Choose ..."
            />
          )}
        />
      </Col>
      {/* Mobile */}
      <Col xs={12} sm={6} md={3} xl={2}>
        <TextInput
          name="mobile_no"
          label="Mobile No."
          control={control}
          placeholder="05xxxxxxxx"
          disabled={view}
          size="sm"
          errors={errors}
          rules={{
            validate: (v) =>
              !v ||
              /^05\d{8}$/.test(v) ||
              "Mobile number must start with 05 and be 10 digits",
          }}
        />
      </Col>

      {/* National ID (Required with nationality check) */}
      <Col xs={12} sm={6} md={3} xl={2}>
        <TextInput
          name="national_id"
          label="National ID"
          control={control}
          size="sm"
          errors={errors}
          required={true}
          disabled={view}
          rules={{
            required: "National ID is required",
            validate: (val) => {
              const nationality = watch("nationality");
              const id = String(val || "").trim();
              if (!/^\d{10}$/.test(id)) return "National ID must be 10 digits";

              if (nationality?.code === "SAU") {
                return id.startsWith("1") ? true : "Saudi ID must start with 1";
              } else {
                return !id.startsWith("1")
                  ? true
                  : "Non-Saudi ID must not start with 1";
              }
            },
          }}
        />
      </Col>

      {/* Email */}
      <Col xs={12} sm={6} md={3} xl={2}>
        <TextInput
          name="email"
          label="Email Address"
          control={control}
          size="sm"
          errors={errors}
          disabled={view}
          rules={{
            validate: (val) =>
              !val ||
              /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val) ||
              "Invalid email address",
          }}
        />
      </Col>

      {/* Speciality (Required) */}
      <Col xs={12} sm={6} md={3} xl={2}>
        <label className="form-label">
          Speciality <span className="text-danger">*</span>
        </label>
        <Controller
          name="speciality"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <MultiSelect
              {...field}
              menuPlacement="auto"
              closeMenuOnSelect={false}
              isMulti={true}
              options={specilaityList?.map((item) => ({
                value: item.specialities_code,
                label: item.specialities_name,
              }))}
              isClearable={true}
              disabled={view}
              value={watch("speciality")}
              onChange={(selected) => setValue("speciality", selected)}
              className={classNames({
                "is-invalid":
                  errors.speciality || (isSubmitted && !watch("speciality")),
              })}
              formatOptionLabel={({ label }) => <div>{label}</div>}
              getOptionValue={(option) => option.value}
              placeholder="Choose ..."
            />
          )}
        />
        {errors?.speciality && <Form.Control.Feedback type="invalid" />}
      </Col>

      {/* Branch (Required) */}
      <Col xs={12} sm={6} md={3} xl={2}>
        <label className="form-label">
          Branches <span className="text-danger">*</span>
        </label>
        <Controller
          name="branch"
          control={control}
          rules={{
            required: "At least one branch is required",
            validate: (v) =>
              v?.length > 0 || "Please select at least one branch",
          }}
          render={({ field }) => (
            <MultiSelect
              {...field}
              isMulti
              disabled={view}
              options={allBranches.map((b) => ({
                value: b.branch_id,
                label: b.branch_name,
                cchi: b.branch_cchino,
              }))}
              value={watch("branch")}
              onChange={(val) => setValue("branch", val)}
              className={classNames({
                "is-invalid": errors.branch,
              })}
              placeholder="Choose branches..."
            />
          )}
        />
      </Col>
    </Row>
  );
};

export default AddPractitionerForm;
